import { PHASE_TYPES, type PhaseType } from '~/models/phase'
import type { Candidate } from './candidate'
import type { DerivedFacts } from './facts'
import { evaluateAestheticPrep } from './rulesets/aestheticPrep'
import { evaluateFatLoss } from './rulesets/fatLoss'
import { evaluateLeanBulk } from './rulesets/leanBulk'
import { evaluateMaintenance } from './rulesets/maintenance'
import { evaluateMiniCut } from './rulesets/miniCut'

/**
 * Which phases this build can actually reason about (D-043).
 *
 * The entry is a total map over `PhaseType` — every phase must appear, and the
 * typechecker enforces that, so adding a sixth phase without deciding its
 * ruleset is a compile error rather than a silent fallback.
 *
 * **There is no default branch anywhere in this file.** A phase whose ruleset
 * is not implemented does not get another phase's logic; it gets nothing, and
 * the caller turns that into the terminal `PhaseRulesUnavailable` outcome. That
 * is the whole point: giving a lean bulk the fat-loss interpretation would be
 * worse than giving no answer at all.
 *
 * General recomposition is absent by design (D-012) and is not a `PhaseType`,
 * so it cannot be written here even by accident.
 */
/** What a ruleset needs beyond the facts to reach a conclusion. */
export interface RulesetContext {
  /** Adherence at the tier D-066 requires before any adjustment is proposed. */
  readonly adherenceIsHigh: boolean
}

export interface RulesetEntry {
  readonly implemented: boolean
  /** Present only when `implemented` is true. */
  readonly evaluate?: (facts: DerivedFacts, context: RulesetContext) => Candidate
}

/**
 * All five Version 1 phases, implemented at Checkpoint 2.4.
 *
 * **Each was flipped only once its matrix in `docs/testing-plan.md` passed**,
 * which is what D-043 requires — selectability follows the ruleset, never the
 * other way round. `SELECTABLE_PHASE_TYPES` below follows automatically, so
 * there is exactly one place to change and no second list to forget.
 *
 * **This does not make the rules engine "complete".** D-043's completeness claim
 * belongs to Checkpoint 2.7, after the export contract catches up and the whole
 * matrix is verified together. Nothing here may be described as finishing
 * Version 1.
 */
export const RULESET_REGISTRY: Readonly<Record<PhaseType, RulesetEntry>> = {
  fatLoss: {
    implemented: true,
    evaluate: (facts, context) => evaluateFatLoss(facts, context.adherenceIsHigh),
  },
  maintenance: { implemented: true, evaluate: evaluateMaintenance },
  leanBulk: { implemented: true, evaluate: evaluateLeanBulk },
  miniCut: { implemented: true, evaluate: evaluateMiniCut },
  aestheticPrep: { implemented: true, evaluate: evaluateAestheticPrep },
}

/** The phases a user may choose right now (D-043, activated at 1.7 per D-052). */
export const SELECTABLE_PHASE_TYPES: readonly PhaseType[] = PHASE_TYPES.filter(
  (type) => RULESET_REGISTRY[type].implemented,
)

export function isPhaseImplemented(type: PhaseType): boolean {
  return RULESET_REGISTRY[type].implemented
}
